import { useId } from "react";

// Deterministic node-edge renderer for the STORED IST machine_map.
// NODES = meaning_units already identified by IST. EDGES = relations already identified by IST.
// It never invents nodes, edges, or labels, and never runs an LLM. Layout is a stable layered
// (hierarchical) placement — the same stored structure always renders the same way.

const TEAL = "#2F6F6A";
const TEAL_SOFT = "#7fa8a4";

// Human-readable words for the relation types IST stores (display only; does not reinterpret them).
const REL_WORDS = {
  CAUSES: "causes", TEMPORAL: "then", CONDITIONAL: "if\u2026then", PART_WHOLE: "part of",
  COMPARATIVE: "compares", IMPLIES: "implies", CONTRAST: "contrasts", RECIPROCAL: "each other",
  TRANSFORM: "becomes", ENABLES: "enables", SUPPORTS: "supports",
};
const relWord = (r) => REL_WORDS[(r || "").toUpperCase()] || (r || "relates").toLowerCase();

const NODE_W = 176, NODE_H = 84, H_GAP = 40, V_GAP = 52, PAD = 20;

const cubicAt = (p0, p1, p2, p3, t) => {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
};

function computeLayout(units, edges) {
  const ids = units.map((u) => u.id);
  const idset = new Set(ids);
  const es = edges.filter((e) => idset.has(e.from) && idset.has(e.to) && e.from !== e.to);

  // Longest-path layering (cycle-safe via bounded relaxation).
  const rank = {};
  ids.forEach((id) => { rank[id] = 0; });
  for (let it = 0; it < ids.length; it++) {
    let changed = false;
    es.forEach((e) => { if (rank[e.to] < rank[e.from] + 1) { rank[e.to] = rank[e.from] + 1; changed = true; } });
    if (!changed) break;
  }
  const maxRank = ids.reduce((m, id) => Math.max(m, rank[id]), 0);

  const layers = [];
  for (let r = 0; r <= maxRank; r++) layers.push(ids.filter((id) => rank[id] === r));

  const idxOf = {};
  ids.forEach((id, i) => { idxOf[id] = i; });
  layers.forEach((layer) => layer.sort((a, b) => idxOf[a] - idxOf[b]));

  // One barycenter pass to reduce edge crossings (deterministic; tie-break by original order).
  const posInLayer = {};
  layers.forEach((layer) => layer.forEach((id, i) => { posInLayer[id] = i; }));
  for (let r = 1; r < layers.length; r++) {
    const bary = {};
    layers[r].forEach((id) => {
      const parents = es.filter((e) => e.to === id).map((e) => posInLayer[e.from]).filter((v) => v != null);
      bary[id] = parents.length ? parents.reduce((s, v) => s + v, 0) / parents.length : posInLayer[id];
    });
    layers[r].sort((a, b) => (bary[a] - bary[b]) || (idxOf[a] - idxOf[b]));
    layers[r].forEach((id, i) => { posInLayer[id] = i; });
  }

  const maxCols = layers.reduce((m, l) => Math.max(m, l.length), 1);
  const rowW = maxCols * NODE_W + (maxCols - 1) * H_GAP;
  const pos = {};
  layers.forEach((layer, r) => {
    const w = layer.length * NODE_W + (layer.length - 1) * H_GAP;
    const startX = PAD + (rowW - w) / 2;
    layer.forEach((id, i) => { pos[id] = { x: startX + i * (NODE_W + H_GAP), y: PAD + r * (NODE_H + V_GAP) }; });
  });
  const W = rowW + PAD * 2;
  const H = layers.length * NODE_H + (layers.length - 1) * V_GAP + PAD * 2;
  return { pos, es, W, H };
}

function NodeBox({ u }) {
  const abs = u.kind === "abstraction";
  const src = u.source && u.source !== "student" ? u.source : null;
  return (
    <div xmlns="http://www.w3.org/1999/xhtml"
      title={(u.label || "") + (abs ? "  (generalization)" : "")}
      style={{ height: "100%", width: "100%", boxSizing: "border-box" }}
      className={`flex flex-col items-center justify-center text-center rounded-md border px-2.5 py-1.5 overflow-hidden ${abs
        ? "border-[#2F6F6A]/55 bg-[#2F6F6A]/[0.08] text-[#204b47]" : "border-stone-300 bg-white text-stone-700"}`}>
      {abs && <span className="block text-[8.5px] font-semibold uppercase tracking-[0.12em] text-[#2F6F6A]/70 mb-0.5">generalization</span>}
      <span className="leading-[1.2]" style={{ fontSize: 12.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{u.label}</span>
      {src && <span className="block text-[8.5px] text-stone-400 mt-0.5">from {src}</span>}
    </div>
  );
}

export function ThinkingStructureMap({ analysis, big }) {
  const uid = useId().replace(/:/g, "");
  const mm = analysis?.machine_map || {};
  const units = mm.meaning_units || [];
  const rels = (mm.relations || []).filter((r) => r.status === "constructed" || r.status === "recoverable");

  if (!units.length) {
    return <div data-testid="thinking-map"><p className="text-[13px] text-stone-400">No ideas extracted for this version.</p></div>;
  }

  // DISCONNECTED — render separateness intentionally; never draw "missing" arrows.
  if (!rels.length) {
    return (
      <div data-testid="thinking-map">
        <div data-testid="thinking-map-disconnected" className="rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/70 p-5">
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 mb-4">Ideas presented separately</p>
          <div className={`flex flex-wrap justify-center ${big ? "gap-6" : "gap-4"}`}>
            {units.map((u) => {
              const abs = u.kind === "abstraction";
              return (
                <div key={u.id} title={u.label}
                  className={`rounded-md border px-3 py-2.5 max-w-[220px] text-center ${big ? "text-[14px]" : "text-[13px]"} leading-snug ${abs
                    ? "border-[#2F6F6A]/55 bg-[#2F6F6A]/[0.08] text-[#204b47]" : "border-stone-300 bg-white text-stone-700"}`}>
                  {abs && <span className="block text-[8.5px] font-semibold uppercase tracking-[0.12em] text-[#2F6F6A]/70 mb-0.5">generalization</span>}
                  {u.label}
                </div>
              );
            })}
          </div>
          <p className="text-center text-[12px] text-stone-500 mt-4">No relationships among these ideas are expressed in this version.</p>
        </div>
      </div>
    );
  }

  const { pos, es, W, H } = computeLayout(units, rels);
  const marker = `arrow-${uid}`;
  const markerSoft = `arrowsoft-${uid}`;

  return (
    <div data-testid="thinking-map">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ maxWidth: big ? W * 1.25 : W, height: "auto", display: "block", margin: "0 auto" }}
        data-testid="thinking-map-svg">
        <defs>
          <marker id={marker} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill={TEAL} />
          </marker>
          <marker id={markerSoft} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill={TEAL_SOFT} />
          </marker>
        </defs>

        {(() => {
          const tgtCount = {};
          es.forEach((e) => { tgtCount[e.to] = (tgtCount[e.to] || 0) + 1; });
          return es.map((e, i) => {
            const s = pos[e.from], t = pos[e.to];
            if (!s || !t) return null;
            let P0, P1, P2, P3, d;
            if (t.y > s.y) { P0 = { x: s.x + NODE_W / 2, y: s.y + NODE_H }; P3 = { x: t.x + NODE_W / 2, y: t.y }; P1 = { x: P0.x, y: (P0.y + P3.y) / 2 }; P2 = { x: P3.x, y: (P0.y + P3.y) / 2 }; }
            else if (t.y < s.y) { P0 = { x: s.x + NODE_W / 2, y: s.y }; P3 = { x: t.x + NODE_W / 2, y: t.y + NODE_H }; P1 = { x: P0.x, y: (P0.y + P3.y) / 2 }; P2 = { x: P3.x, y: (P0.y + P3.y) / 2 }; }
            else if (t.x > s.x) { P0 = { x: s.x + NODE_W, y: s.y + NODE_H / 2 }; P3 = { x: t.x, y: t.y + NODE_H / 2 }; P1 = { x: (P0.x + P3.x) / 2, y: P0.y }; P2 = { x: (P0.x + P3.x) / 2, y: P3.y }; }
            else { P0 = { x: s.x, y: s.y + NODE_H / 2 }; P3 = { x: t.x + NODE_W, y: t.y + NODE_H / 2 }; P1 = { x: (P0.x + P3.x) / 2, y: P0.y }; P2 = { x: (P0.x + P3.x) / 2, y: P3.y }; }
            d = `M ${P0.x} ${P0.y} C ${P1.x} ${P1.y}, ${P2.x} ${P2.y}, ${P3.x} ${P3.y}`;

            const recoverable = e.status === "recoverable";
            const n = tgtCount[e.to];
            // Converging edges: place the label near its (well-separated) SOURCE, not the shared target,
            // so parallel labels never crowd at the convergence point.
            const tv = n > 1 ? 0.26 : 0.5;
            const lp = cubicAt(P0, P1, P2, P3, tv);
            const label = relWord(e.relation);
            const lw = label.length * 6.1 + 12;
            return (
              <g key={i} data-testid={`map-edge-${e.from}-${e.to}`}>
                <path d={d} fill="none" stroke={recoverable ? TEAL_SOFT : TEAL} strokeWidth={recoverable ? 1.5 : 2}
                  strokeDasharray={recoverable ? "5 4" : undefined} markerEnd={`url(#${recoverable ? markerSoft : marker})`} />
                <g transform={`translate(${lp.x}, ${lp.y})`}>
                  <rect x={-lw / 2} y={-9} width={lw} height={18} rx={5} fill="#ffffff" stroke="#e7e5e4" />
                  <text x={0} y={3.5} textAnchor="middle" fontSize="10.5" fill={recoverable ? "#5f817e" : TEAL}
                    fontFamily="ui-monospace, monospace">{label}</text>
                </g>
              </g>
            );
          });
        })()}

        {units.map((u) => {
          const p = pos[u.id];
          if (!p) return null;
          return (
            <foreignObject key={u.id} x={p.x} y={p.y} width={NODE_W} height={NODE_H} data-testid={`map-node-${u.id}`}>
              <NodeBox u={u} />
            </foreignObject>
          );
        })}
      </svg>
      {rels.some((r) => r.status === "recoverable") && (
        <p className="mt-2 text-center text-[11px] text-stone-400" data-testid="thinking-map-legend">
          <span className="inline-block align-middle mr-1" style={{ width: 22, borderTop: "1.5px dashed #7fa8a4" }} /> dashed = relationship implied rather than stated outright
        </p>
      )}
    </div>
  );
}
